/**
 * HCP sign-up wizard: env-driven client validation & UX flags (CRA: REACT_APP_* only).
 * Defaults match previous hard-coded behaviour when vars are unset.
 */

function envBool(key, defaultValue = false) {
  const v = process.env[key];
  if (v == null || String(v).trim() === '') return defaultValue;
  return /^(1|true|yes|on)$/i.test(String(v).trim());
}

function envInt(key, defaultValue) {
  const v = process.env[key];
  if (v == null || String(v).trim() === '') return defaultValue;
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : defaultValue;
}

function envString(key, defaultValue = '') {
  const v = process.env[key];
  if (v == null || String(v).trim() === '') return defaultValue;
  return String(v).trim();
}

/** @param {string} raw comma-separated extensions without dots */
function envAcceptList(key, fallbackCsv) {
  const s = envString(key, '');
  const src = s || fallbackCsv;
  return src
    .split(',')
    .map((x) => x.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);
}

/**
 * Flattened flags consumed by `buildGeneralInfoSchema` / `validationConfigDefaults`.
 */
export function getHcpSignupEnvValidationConfig() {
  return {
    genderRequired: envBool('REACT_APP_HCP_SIGNUP_GENDER_REQUIRED', true),
    addressCountryRequired: envBool(
      'REACT_APP_HCP_SIGNUP_ADDRESS_COUNTRY_REQUIRED',
      true
    ),
    addressStateRequired: envBool(
      'REACT_APP_HCP_SIGNUP_ADDRESS_STATE_REQUIRED',
      true
    ),
    addressCityRequired: envBool(
      'REACT_APP_HCP_SIGNUP_ADDRESS_CITY_REQUIRED',
      true
    ),
    addressStreetRequired: envBool(
      'REACT_APP_HCP_SIGNUP_ADDRESS_STREET_REQUIRED',
      true
    ),
    addressZipRequired: envBool(
      'REACT_APP_HCP_SIGNUP_ADDRESS_ZIP_REQUIRED',
      true
    ),

    firstNameMinLength: envInt('REACT_APP_HCP_SIGNUP_FIRST_NAME_MIN', 2),
    firstNameMaxLength: envInt('REACT_APP_HCP_SIGNUP_FIRST_NAME_MAX', 80),
    lastNameMinLength: envInt('REACT_APP_HCP_SIGNUP_LAST_NAME_MIN', 2),
    lastNameMaxLength: envInt('REACT_APP_HCP_SIGNUP_LAST_NAME_MAX', 80),

    /** Min age in full years; 0 = disabled */
    dobMinAgeYears: envInt('REACT_APP_HCP_SIGNUP_DOB_MIN_AGE_YEARS', 0),

    idNumberMinLength: envInt('REACT_APP_HCP_SIGNUP_ID_NUMBER_MIN', 6),
    idNumberMaxLength: envInt('REACT_APP_HCP_SIGNUP_ID_NUMBER_MAX', 20),
    /** "alphanumeric" | "numeric" */
    idNumberPattern: envString(
      'REACT_APP_HCP_SIGNUP_ID_NUMBER_PATTERN',
      'alphanumeric'
    ),

    phoneMinDigits: envInt('REACT_APP_HCP_SIGNUP_PHONE_MIN_DIGITS', 7),
    phoneMaxDigits: envInt('REACT_APP_HCP_SIGNUP_PHONE_MAX_DIGITS', 15),

    addressStreetMinLength: envInt(
      'REACT_APP_HCP_SIGNUP_ADDRESS_STREET_MIN',
      3
    ),
    addressStreetMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_ADDRESS_STREET_MAX',
      200
    ),
    addressZipMinLength: envInt('REACT_APP_HCP_SIGNUP_ADDRESS_ZIP_MIN', 3),
    addressZipMaxLength: envInt('REACT_APP_HCP_SIGNUP_ADDRESS_ZIP_MAX', 12),

    idImageRequired: envBool('REACT_APP_HCP_SIGNUP_ID_IMAGE_REQUIRED', true),
    idImageMaxMb: envInt('REACT_APP_HCP_SIGNUP_ID_IMAGE_MAX_MB', 10),
    idImageAcceptExt: envAcceptList(
      'REACT_APP_HCP_SIGNUP_ID_IMAGE_ACCEPT',
      'pdf,jpg,jpeg,png'
    ),
  };
}

export function getHcpSignupEnvLicenseFieldConfig() {
  const prefixRequired = envBool(
    'REACT_APP_HCP_SIGNUP_LICENSE_PREFIX_REQUIRED',
    false
  );
  return {
    prefixRequired,
    prefixOptional: !prefixRequired,
    prefixMinLength: envInt('REACT_APP_HCP_SIGNUP_LICENSE_PREFIX_MIN', 1),
    prefixMaxLength: envInt('REACT_APP_HCP_SIGNUP_LICENSE_PREFIX_MAX', 10),
    licenseNumberMinLength: envInt(
      'REACT_APP_HCP_SIGNUP_LICENSE_NUMBER_MIN',
      4
    ),
    licenseNumberMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_LICENSE_NUMBER_MAX',
      20
    ),
    licenseNumberPattern: envString(
      'REACT_APP_HCP_SIGNUP_LICENSE_NUMBER_PATTERN',
      'alphanumeric'
    ),
    authorityNameMinLength: envInt(
      'REACT_APP_HCP_SIGNUP_LICENSE_AUTHORITY_MIN',
      2
    ),
    authorityNameMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_LICENSE_AUTHORITY_MAX',
      120
    ),
    licenseAttachmentRequired: envBool(
      'REACT_APP_HCP_SIGNUP_LICENSE_ATTACHMENT_REQUIRED',
      true
    ),
    licenseAttachmentMaxMb: envInt(
      'REACT_APP_HCP_SIGNUP_LICENSE_ATTACHMENT_MAX_MB',
      10
    ),
    licenseAttachmentAcceptExt: envAcceptList(
      'REACT_APP_HCP_SIGNUP_LICENSE_ATTACHMENT_ACCEPT',
      'pdf,jpg,jpeg,png'
    ),
  };
}

export function getHcpSignupEnvEducationConfig() {
  return {
    countryOfCertificateRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_COUNTRY_REQUIRED',
      true
    ),
    degreeMinLength: envInt('REACT_APP_HCP_SIGNUP_EDU_DEGREE_MIN', 2),
    degreeMaxLength: envInt('REACT_APP_HCP_SIGNUP_EDU_DEGREE_MAX', 120),
    institutionMinLength: envInt('REACT_APP_HCP_SIGNUP_EDU_INSTITUTION_MIN', 2),
    institutionMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_INSTITUTION_MAX',
      200
    ),
    authorityNameRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_AUTHORITY_REQUIRED',
      true
    ),
    authorityNameMinLength: envInt('REACT_APP_HCP_SIGNUP_EDU_AUTHORITY_MIN', 2),
    authorityNameMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_AUTHORITY_MAX',
      200
    ),
    fieldOfStudyRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_FIELD_OF_STUDY_REQUIRED',
      false
    ),
    fieldOfStudyMinLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_FIELD_OF_STUDY_MIN',
      2
    ),
    fieldOfStudyMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_FIELD_OF_STUDY_MAX',
      200
    ),
    studyYearsRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_STUDY_YEARS_REQUIRED',
      true
    ),
    studyYearsMin: envInt('REACT_APP_HCP_SIGNUP_EDU_STUDY_YEARS_MIN', 1),
    studyYearsMax: envInt('REACT_APP_HCP_SIGNUP_EDU_STUDY_YEARS_MAX', 10),
    institutionLocationRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_INSTITUTION_LOCATION_REQUIRED',
      false
    ),
    institutionLocationMinLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_INSTITUTION_LOCATION_MIN',
      2
    ),
    institutionLocationMaxLength: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_INSTITUTION_LOCATION_MAX',
      120
    ),
    accreditationStatusRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EDU_ACCREDITATION_STATUS_REQUIRED',
      true
    ),
    accreditationDocumentMaxMb: envInt(
      'REACT_APP_HCP_SIGNUP_EDU_ACCREDITATION_DOC_MAX_MB',
      10
    ),
    accreditationDocumentAcceptExt: envAcceptList(
      'REACT_APP_HCP_SIGNUP_EDU_ACCREDITATION_DOC_ACCEPT',
      'pdf,jpg,jpeg,png'
    ),
  };
}

/** Professional experience (Step 3): training, certificates, optional relevant files.
 *  Server-side validation (signup/profile APIs) reads the same rules from HCP_SIGNUP_EXP_*
 *  variables in wfe-be (see utils/hcpExperienceValidationConfig.js).
 */
export function getHcpSignupEnvExperienceConfig() {
  return {
    internshipTrainingRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_TRAINING_REQUIRED',
      true
    ),
    internshipCertificateRequiredWhenInternshipCompletionYes: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_CERT_REQUIRED_WHEN_COMPLETION_YES',
      envBool(
        'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_CERT_REQUIRED_WHEN_TRAINING_YES',
        true
      )
    ),
    /** @deprecated Use internshipCertificateRequiredWhenInternshipCompletionYes; kept for compatibility */
    internshipCertificateWhenTrainingYes: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_CERT_REQUIRED_WHEN_TRAINING_YES',
      true
    ),
    postGradInternRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_POST_GRAD_INTERN_REQUIRED',
      false
    ),
    internshipCompletionRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_COMPLETION_REQUIRED',
      false
    ),
    additionalExperienceSectionEnabled: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_ADDITIONAL_EXPERIENCE_SECTION',
      true
    ),
    /** When training is Yes, additional narrative field */
    additionalExperienceRequiredWhenInternshipYes: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_ADDITIONAL_EXPERIENCE_REQUIRED_WHEN_TRAINING_YES',
      true
    ),
    undergraduateInternCertificateRequired: envBool(
      'REACT_APP_HCP_SIGNUP_EXP_UNDERGRAD_CERT_REQUIRED',
      false
    ),
    relevantAttachmentsMax: envInt(
      'REACT_APP_HCP_SIGNUP_EXP_RELEVANT_ATTACHMENTS_MAX',
      20
    ),
    relevantAttachmentsMaxMb: envInt(
      'REACT_APP_HCP_SIGNUP_EXP_RELEVANT_ATTACHMENTS_MAX_MB',
      10
    ),
    relevantAttachmentsAcceptExt: envAcceptList(
      'REACT_APP_HCP_SIGNUP_EXP_RELEVANT_ATTACHMENTS_ACCEPT',
      'pdf,jpg,jpeg,png'
    ),
    internshipCertificateMaxMb: envInt(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_CERT_MAX_MB',
      10
    ),
    internshipCertificateAcceptExt: envAcceptList(
      'REACT_APP_HCP_SIGNUP_EXP_INTERNSHIP_CERT_ACCEPT',
      'pdf,jpg,jpeg,png'
    ),
  };
}
